import { isValidTopicSlug } from '../../domain/topicSlugPolicy';

const ACTIONS = {
	// Compatibility for notifications persisted before Settings was retired.
	'/settings': "showView('profile');setActiveTab('profile')",
	'/profile': "showView('profile');setActiveTab('profile')",
	'/community': "showView('community');setActiveTab('community')",
	'/referrals': "showView('referrals');setActiveTab('profile')",
	'/ai': "showView('ai-create');setActiveTab('ai-create')",
} as const;

export type NotificationActionRoute = keyof typeof ACTIONS;

const FEEDBACK_TICKET_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Accepts only the server-owned, exact feedback ticket path. */
export function feedbackTicketIdFromPath(value: string | null | undefined): string | null {
	if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
	const feedback = new URL(value, 'https://notifications.deepmox.invalid');
	if (feedback.pathname !== '/feedback' || feedback.searchParams.size !== 1) return null;
	const ticketId = feedback.searchParams.get('ticket');
	return ticketId && FEEDBACK_TICKET_ID.test(ticketId) ? ticketId : null;
}

/** Reads the current browser location without accepting an external URL. */
export function feedbackTicketIdFromBrowserLocation(location: Pick<Location, 'pathname' | 'search'>): string | null {
	return feedbackTicketIdFromPath(`${location.pathname}${location.search}`);
}

/** Rejects everything except the five server-owned, exact same-origin SPA routes. */
export function notificationPrototypeAction(value: string | null | undefined): string | null {
	const ticketId = feedbackTicketIdFromPath(value);
	if (ticketId) return `showView('feedback');setActiveTab('profile');openFeedbackNotificationTicket('${ticketId}')`;
	if (!value) return null;
	if (!Object.hasOwn(ACTIONS, value)) return null;
	return ACTIONS[value as NotificationActionRoute];
}

/**
 * The only notification that may navigate directly is a server-produced
 * Community Topic completion. Keep this deliberately narrower than the
 * historical allowlist: no generic notification CTA and no arbitrary URL.
 */
export function topicReadyPathFromNotification(eventType: string, actionUrl: string | null | undefined): string | null {
	if (eventType !== 'community.idea_completed' || !actionUrl || !actionUrl.startsWith('/topics/')) return null;
	const topicSlug = actionUrl.slice('/topics/'.length);
	return isValidTopicSlug(topicSlug) ? actionUrl : null;
}
